import { AfterViewInit, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { NavbarComponent } from '../navbar/navbar.component';
import { FooterComponent } from '../footer/footer.component';
import { OAuthService, OAuthEvent } from 'angular-oauth2-oidc';
import { filter, fromEvent, interval } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthServiceService } from '../../services/auth-service/auth-service.service';
import { ResponseUser } from '../../../shared/models/utente';
import { CookieConsentComponent, SnackbarService, STATISTICS_SERVICES } from '@mic-ac/ui-kit';
import { HOME_ROUTE } from '../../../app-route-paths';
import { NavbarUiService } from '../navbar/navbar-ui.service';
import { ContactRequestModalComponent } from '../../../features/contact/contact-request-modal.component';

type NavItem = {
  label: string;
  path: string;
  exact: boolean;
  loggedIn: boolean;
}
@Component({
    selector: 'app-shell',
    imports: [RouterOutlet, RouterLink, RouterLinkActive, NavbarComponent, FooterComponent, CookieConsentComponent, ContactRequestModalComponent],
    templateUrl: './shell.component.html',
    styleUrl: './shell.component.css'
})
export class ShellComponent implements OnInit, AfterViewInit {

  #oauth = inject(OAuthService);
  #router = inject(Router);
  #activeRoute = inject(ActivatedRoute);
  #destroyRef = inject(DestroyRef);
  #auth = inject(AuthServiceService);
  #snackbar = inject(SnackbarService);
  #navbarUi = inject(NavbarUiService);

  private preheaderRelocated = false;

  shellLayout: 'default' | 'map' | 'landing' = 'default';
  /** Hide the footer's own back-to-top button on pages that provide their own. */
  showFooterBackToTop = signal(true);
  userProfile = signal<ResponseUser>({} as ResponseUser);
  showCookiePreferences = signal(false);
  showContactModal = signal(false);
  footerInView = signal(false);
  readonly statisticsServices = STATISTICS_SERVICES;

  private footerObserver: IntersectionObserver | null = null;

  mobileMenuOpen = this.#navbarUi.mobileMenuOpen;
  isAuthenticated = computed(
    () => this.#auth.user() !== null || this.#auth.iamOnlyUser(),
  );
  isIamOnlyUser = computed(() => this.#auth.iamOnlyUser());

  labelValueMyArea = computed(() =>
    this.userProfile().element?.micAuthorized === true ? 'Gestione collezioni' : 'Accreditamento'
  );

  isLoggedIn = computed(() => this.#auth.user() !== null);

  navItems = computed(() => [
    { label: 'Home', path: HOME_ROUTE, exact: true, loggedIn: true },
    { label: 'Il progetto', path: 'approfondimento', exact: false, loggedIn: true },
    { label: this.labelValueMyArea(), path: 'mp/my-area', exact: true, loggedIn: this.isLoggedIn() },
  ]);

  footerNavItems = computed(() =>
    this.navItems()
      .filter(i => i.loggedIn)
      .map(({ label, path }) => ({ label, path }))
  );

  visibleNavItems = computed(() => this.navItems().filter(item => item.loggedIn));

  logoNavbar = '../../../../assets/icons/Ecomic_Logo_Positivo.svg';
  logoFooterWordmark = '../../../../assets/icons/Ecomic_Wordmark_White.png';
  logoFooterWordmark2x = '../../../../assets/icons/Ecomic_Wordmark_White@2x.png';
  appLogoLink = 'https://ecomic.cultura.gov.it/';

  logoLinkedin = '../../../../assets/img/logo-linkedin.png';
  logoUpperBar = '../../../../assets/img/icona-toolbar.png';
  logoFooter = '../../../../assets/img/Logo_ecomic_footer.png';
  logoColLeft1 = '../../../../assets/img/europa.png'
  logoColLeft2 = '../../../../assets/img/logo_ministero_cultura_footer.png'
  logoColLeft3 = '../../../../assets/img/direzione.png'
  logoColLeft4 = '../../../../assets/img/digital_lib.png'
  logoColRight = '../../../../assets/img/Logo_ecomic_footer.png'

  logoColLeft1Link = 'https://next-generation-eu.europa.eu/index_it'
  logoColLeft2Link = 'https://cultura.gov.it/'
  logoColLeft3Link = 'https://dgdc.cultura.gov.it/'
  logoColLeft4Link = 'https://digitallibrary.cultura.gov.it/'
  logoColRightLink = 'https://ecomic.cultura.gov.it/'

  getProfile() {
    this.#auth.myProfile().subscribe({
      next: (res) => {
        this.userProfile.set(res);
        this.#auth.setUser(res);
      },
      error: (err) => {
        if (err?.status === 400) {
          this.#snackbar.show('Registrazione necessaria', 'warning');
          this.#auth.logoutToRegister();
          this.#router.navigate(['/register']);
        }
      },
    });
  }

  onCookieConsentClosed(): void {
    this.showCookiePreferences.set(false);
  }

  openCookiePreferences(): void {
    this.showCookiePreferences.set(true);
  }

  navigateToCookiePolicy(): void {
    this.showCookiePreferences.set(false);
    this.#router.navigate(['/cookie-policy']);
  }

  navigateToPrivacyPolicy(): void {
    this.showCookiePreferences.set(false);
    this.#router.navigate(['/privacy-policy']);
  }

  closeMobileMenu(): void {
    this.#navbarUi.closeMobileMenu();
  }

  mobileLogin(): void {
    this.closeMobileMenu();
    this.#auth.login();
  }

  mobileLogout(): void {
    this.closeMobileMenu();
    this.#auth.logout();
  }

  ngOnInit(): void {
    this.applyShellLayout();

    fromEvent<CustomEvent>(window, 'navigate-to-privacy-policy')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.showCookiePreferences.set(false);
        this.#router.navigate(['/privacy-policy']);
      });

    fromEvent<CustomEvent>(window, 'navigate-to-cookie-policy')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.showCookiePreferences.set(false);
        this.#router.navigate(['/cookie-policy']);
      });

    fromEvent<CustomEvent>(window, 'open-contact-form')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.showContactModal.set(true);
      });

    setTimeout(() => this.checkPostAuthSnackbar(), 0);

    const token = this.#oauth.getAccessToken();

    if (token && !this.#auth.iamOnlyUser()) {
      this.getProfile();
    }

    this.#router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.applyShellLayout();
        this.checkPostAuthSnackbar();
        this.closeMobileMenu();
        queueMicrotask(() => this.setupFooterVisibilityObserver());
      });

    this.monitorToken();

    this.#destroyRef.onDestroy(() => this.footerObserver?.disconnect());
  }

  ngAfterViewInit(): void {
    this.relocatePreheaderAnchor();
    requestAnimationFrame(() => this.relocatePreheaderAnchor());
    this.setupFooterVisibilityObserver();

    // toolbar.js può iniettare la pre-header dopo il primo render dello shell
    const appRoot = document.querySelector('app-root');
    if (appRoot) {
      const observer = new MutationObserver(() => this.relocatePreheaderAnchor());
      observer.observe(appRoot, { childList: true, subtree: true });
      this.#destroyRef.onDestroy(() => observer.disconnect());
    }
  }

  /**
   * Sposta la pre-header Ecomic dentro lo shell, subito sopra la navbar.
   * Con la pre-header fuori da app-shell, `position: sticky; top: var(--preheader-h)`
   * sulla navbar crea un doppio offset (gap bianco) a scroll 0.
   */
  private relocatePreheaderAnchor(): void {
    if (this.preheaderRelocated) {
      return;
    }

    const shellRoot = document.querySelector('app-shell .app-shell-root');
    const navbarHeader = document.querySelector('app-shell header.app-shell-header');
    const anchor = document.querySelector('app-root > header.ecomic-toolbar-anchor')
      ?? document.querySelector('header.ecomic-toolbar-anchor');

    if (!anchor || !shellRoot || !navbarHeader) {
      return;
    }

    if (shellRoot.contains(anchor)) {
      this.preheaderRelocated = true;
      return;
    }

    shellRoot.insertBefore(anchor, navbarHeader);
    this.preheaderRelocated = true;
    window.dispatchEvent(new Event('resize'));
  }

  private checkPostAuthSnackbar(): void {
    if (sessionStorage.getItem('post_login_snackbar') === 'true') {
      sessionStorage.removeItem('post_login_snackbar');
      this.#snackbar.show('Accesso effettuato con successo', 'success');
    } else if (sessionStorage.getItem('post_register_snackbar') === 'true') {
      sessionStorage.removeItem('post_register_snackbar');
      this.#snackbar.show('Registrazione completata con successo', 'success');
    }
  }

  private monitorToken(): void {
    this.#oauth.events
      .pipe(
        filter((e: OAuthEvent) =>
          e.type === 'token_expires' ||
          e.type === 'token_error' ||
          e.type === 'session_terminated'
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        if (!this.#oauth.hasValidAccessToken()) {
          this.#auth.relogin();
        }
      });

    interval(30_000)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        const hasToken = !!this.#oauth.getAccessToken();
        if (hasToken && !this.#oauth.hasValidAccessToken()) {
          this.#auth.relogin();
        }
      });
  }

  private applyShellLayout(): void {
    const data = this.getDeepestRouteData();
    this.shellLayout =
      (data?.['shellLayout'] as 'default' | 'map' | 'landing' | undefined) ?? 'default';
    this.showFooterBackToTop.set(data?.['hideFooterBackToTop'] !== true);
    this.setupFooterVisibilityObserver();
  }

  /**
   * Il banner cookie compare solo quando il footer è visibile in viewport.
   * Dopo un cambio layout (es. map → landing/approfondimento) `app-footer` entra nel DOM
   * solo al ciclo di change detection successivo: senza retry l'observer non si aggancia
   * e il banner resta nascosto fino al refresh.
   */
  private setupFooterVisibilityObserver(): void {
    this.footerObserver?.disconnect();
    this.footerObserver = null;

    if (this.shellLayout === 'map') {
      this.footerInView.set(false);
      return;
    }

    const attach = (): boolean => {
      const footer = document.querySelector('app-footer .footer-container');
      if (!footer) {
        this.footerInView.set(false);
        return false;
      }

      this.footerObserver?.disconnect();
      this.footerObserver = new IntersectionObserver(
        ([entry]) => this.footerInView.set(entry.isIntersecting),
        { threshold: 0.05 },
      );
      this.footerObserver.observe(footer);
      return true;
    };

    if (attach()) {
      return;
    }

    // Attende il render del @if (map ↔ app-footer), poi un secondo tentativo post-paint.
    queueMicrotask(() => {
      if (attach()) {
        return;
      }
      requestAnimationFrame(() => {
        if (!attach()) {
          setTimeout(() => attach(), 0);
        }
      });
    });
  }

  private getDeepestRouteData(): Record<string, unknown> | null {
    let currentRoute = this.#activeRoute.firstChild;

    while (currentRoute?.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    return currentRoute?.snapshot.data ?? null;
  }
}
